import dayjs from "dayjs";
import styled from "styled-components";
import React, { useState, useEffect, useRef } from "react";
import { StringParam, NumberParam, withDefault, useQueryParams } from "use-query-params";
import { Row, Col, Table, Input, Select, Empty, DatePicker, ConfigProvider, Button } from "antd";

import type { ColumnsType } from "antd/es/table";
import type { RangePickerProps } from "antd/es/date-picker";

import { TransactionNIBSS } from "components/Status";
import { ApiErrorTableComponent } from "components/ErrorHandler";

import { TransactionDTO } from "models";
import { useAppDispatch } from "app/hooks";
import { openNotificationWithIcon } from "utils";
import { useGetSortCodeTypeEnumQuery } from "app/configApiSlice";
import { updateTransactionReportParams } from "pages/Report/reportSlice";
import { useLazyGetTransactionReportQuery } from "pages/Report/reportApiSlice";

const { RangePicker } = DatePicker;

interface IRangePickerProps {
  startDate?: string;
  endDate?: string;
  onOpen?: boolean;
}

const ReportSettlementContainer = styled.div`
  .ant-table-content {
    border-radius: 4px;
  }

  colgroup {
    border-radius: 4px;
    col {
      border-radius: 4px;
    }
  }
`;

const ReportSettlementTable: React.FC<any> = () => {
  const dispatch = useAppDispatch();
  const isFirstTimeRendered = useRef(true);
  const sortCode = useGetSortCodeTypeEnumQuery();

  const [rangePickerProps, setRangePickerProps] = useState<IRangePickerProps>();

  const [transactionsReportParams, setTransactionsReportParams] = useQueryParams({
    page: withDefault(NumberParam, 0),
    size: withDefault(NumberParam, 10),
    status: withDefault(StringParam, ""),
    batchId: withDefault(StringParam, ""),
    merchant: withDefault(StringParam, ""),
    sortCode: withDefault(StringParam, ""),
    endTransmittedDate: withDefault(StringParam, ""),
    startTransmittedDate: withDefault(StringParam, ""),
  });

  const [getTransactionReport, getTransactionReportResult] = useLazyGetTransactionReportQuery();

  const sortCodeOptions = sortCode?.data?.response?.map
    ? sortCode?.data?.response?.map(({ code, bankName }) => ({
        value: code,
        label: bankName,
      }))
    : [];

  const handleGetTransactionReport = () => {
    getTransactionReport(transactionsReportParams as any);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    setTransactionsReportParams({
      size: pagination.pageSize,
      page: pagination.current - 1,
    });
  };

  const handleFilterChange = (
    name: string = "",
    value: {
      label?: string;
      value?: string;
      key?: string;
      disabled?: boolean;
    },
  ) => {
    setTransactionsReportParams({
      [name]: value.value,
    });
  };

  useEffect(() => {
    if (!isFirstTimeRendered.current) {
      handleGetTransactionReport();
    }

  }, [transactionsReportParams.page, transactionsReportParams.size, handleGetTransactionReport]);

  useEffect(() => {
    isFirstTimeRendered.current = false;
  }, []);

  useEffect(() => {
    const { size, page, ...transactionParams } = transactionsReportParams;
    dispatch(updateTransactionReportParams(transactionParams));
  }, [transactionsReportParams, dispatch]);

  useEffect(() => {
    if (rangePickerProps?.onOpen) {
      handleFilterChange("startTransmittedDate", {
        value: rangePickerProps.startDate,
      });
      handleFilterChange("endTransmittedDate", {
        value: rangePickerProps.endDate,
      });
    }
  }, [rangePickerProps, handleFilterChange]);

  useEffect(() => {
    if (getTransactionReportResult.isError) {
      const error = getTransactionReportResult?.error as { status: number; data: { error?: string; message?: string; errorMessage?: string; errorMessageList?: string[] } };
      if (error.status >= 500 && error.status < 600) {
        openNotificationWithIcon(error.data.error, error.data.message, "error");
      } else if (error.status === 400) {
        error.data?.errorMessageList?.length
          ? error.data?.errorMessageList?.forEach((errorMessage: string) =>
              openNotificationWithIcon(error.data.errorMessage, errorMessage, "warning"),
            )
          : openNotificationWithIcon(error.data.errorMessage, "", "warning");
      }
    }
  }, [getTransactionReportResult.isError, getTransactionReportResult.error]);

  const columns: ColumnsType<TransactionDTO> = [
    {
      title: "Batch ID",
      dataIndex: "batchID",
      key: "batchID",
      width: "210px",
    },

    {
      title: "Merchant ID",
      dataIndex: "merchant",
      key: "merchant",
      width: "150px",
    },

    {
      title: "Account Name",
      dataIndex: "accountName",
      key: "accountName",
      width: "280px",
    },
    {
      title: "Account Number",
      dataIndex: "accountNumber",
      key: "accountNumber",
      width: "150px",
      align: "center",
    },

    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "110px",
      align: "right",
    },

    {
      title: "Transmitted Date",
      dataIndex: "transmittedDate",
      key: "transmittedDate",
      width: "220px",
      align: "center",
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm A") : ""),
    },

    {
      title: "Sort Code",
      dataIndex: "sortCode",
      key: "sortCode",
      width: "130px",
    },

    {
      title: "Payor ",
      dataIndex: "payor",
      key: "payor",
      width: "150px",
    },

    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: "100px",
      align: "center",
    },

    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      key: "status",
      width: "150px",
      fixed: "right",
      render: (value) => <TransactionNIBSS status={value} />,
    },
  ];

  const disabledReportDate: RangePickerProps["disabledDate"] = (current) => {
    return current && current > dayjs().endOf("day");
  };

  return (
    <ReportSettlementContainer>
      <Row justify="space-between" style={{ padding: "15px 0px" }}>
        <Col>
          <Row gutter={10}>
            <Col>
              <Select
                showSearch
                size="large"
                labelInValue={true}
                suffixIcon="SORTCODE"
                style={{ width: 250 }}
                optionFilterProp="children"
                defaultValue={{ value: "" }}
                options={[{ value: "", label: " All", key: "all" }, ...sortCodeOptions]}
                filterOption={(input, option) => {
                  return (option?.label?.toLowerCase() ?? "").includes(input.toLowerCase());
                }}
                filterSort={(optionA, optionB) => {
                  return (optionA?.label?.toLowerCase() ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label?.toLowerCase() ?? "").toLowerCase());
                }}
                onChange={(value: {
                  key?: string;
                  label?: string;
                  value?: string;
                  disabled?: boolean;
                }) => handleFilterChange("sortCode", value)}
              />
            </Col>

            <Col>
              <RangePicker
                size="large"
                format="YYYY-MM-DD"
                disabledDate={disabledReportDate}
                onOpenChange={(value) => {
                  if (value) {
                    setRangePickerProps((prev) => ({
                      ...prev,
                      onOpen: value,
                    }));
                  }
                }}
                onChange={(dates, dateString) =>
                  setRangePickerProps((prev) => ({
                    ...prev,
                    startDate: dateString[0],
                    endDate: dateString[1],
                  }))
                }
              />
            </Col>
          </Row>
        </Col>

        <Col>
          <Row gutter={10}>
            <Col>
              <Input
                allowClear
                size="large"
                defaultValue=""
                style={{ width: 270 }}
                addonBefore="MerchantID"
                placeholder="Enter Merchant ID"
                onChange={(e) => handleFilterChange("merchant", { value: e.target.value })}
              />
            </Col>

            <Col>
              <Button
                size="large"
                type="primary"
                style={{ width: 130 }}
                onClick={() => handleGetTransactionReport()}
              >
                Search
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <ConfigProvider
        renderEmpty={() =>
          getTransactionReportResult.isError ? (
            <ApiErrorTableComponent
              refetch={handleGetTransactionReport}
              queryResult={getTransactionReportResult}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )
        }
      >
        <Table
          key="uuid"
          columns={columns}
          scroll={{ x: 1600 }}
          rowKey={(record: TransactionDTO) => record?.id || ""}
          dataSource={
            getTransactionReportResult.isError
              ? []
              : getTransactionReportResult.data?.response.content
          }
          pagination={{
            showTitle: true,
            showSizeChanger: true,
            showQuickJumper: true,
            showPrevNextJumpers: true,
            pageSizeOptions: [5, 10, 20, 50, 100, 200],
            pageSize: getTransactionReportResult.data?.response.size,
            total: getTransactionReportResult.data?.response.totalElements,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          loading={getTransactionReportResult.isFetching}
          onChange={handleTableChange}
        />
      </ConfigProvider>
    </ReportSettlementContainer>
  );
};

export default ReportSettlementTable;
